import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";

import { Alert } from "../../components/ui/Alert";
import { Button } from "../../components/ui/Button";
import { DateTimeField } from "../../components/ui/DateTimeField";
import { FormField } from "../../components/ui/FormField";
import { Input } from "../../components/ui/Input";
import { UserSelect } from "../../components/ui/UserSelect";
import { ApiError } from "../../lib/api";
import { type IssuedToken, enrollmentApi } from "../../lib/api/enrollment";
import { type UserSelectOption } from "../../lib/api/users";
import { t } from "../../lib/i18n";

interface CreateTokensFormProps {
  batchId: string;
  onIssued: (tokens: IssuedToken[]) => void;
}

export function CreateTokensForm({ batchId, onIssued }: CreateTokensFormProps) {
  const [count, setCount] = useState<string>("1");
  const [userId, setUserId] = useState<string>("");
  const [, setUserOption] = useState<UserSelectOption | null>(null);
  const [expiresAt, setExpiresAt] = useState<string>("");

  // When a user is bound, force count=1 to keep semantics clear in Phase 7.
  const effectiveCount = userId ? "1" : count;

  const mutate = useMutation({
    mutationFn: () =>
      enrollmentApi.issueTokens(batchId, {
        count: Number(effectiveCount || "1"),
        user_id: userId || null,
        expires_at: new Date(expiresAt).toISOString(),
      }),
    onSuccess: (data) => onIssued(data.tokens),
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!expiresAt) return;
    mutate.mutate();
  };

  const errMsg =
    mutate.error instanceof ApiError
      ? t(`errors.${mutate.error.code}`, mutate.error.message)
      : mutate.error
        ? t("errors.network")
        : null;

  return (
    <form onSubmit={onSubmit} style={{ display: "grid", gap: 16, gridTemplateColumns: "1fr 1fr" }}>
      {errMsg && (
        <div style={{ gridColumn: "1 / -1" }}>
          <Alert tone="danger">{errMsg}</Alert>
        </div>
      )}

      <div style={{ gridColumn: "1 / -1" }}>
        <Alert tone="info">{t("enrollment.userBinding.guidance")}</Alert>
      </div>

      <FormField label={t("enrollment.userBinding.userLabel")} required>
        {(id) => (
          <UserSelect
            id={id}
            value={userId}
            onChange={(nextId, opt) => {
              setUserId(nextId);
              setUserOption(opt);
            }}
            role="member"
            required
          />
        )}
      </FormField>

      <FormField label={t("enrollment.tokens.field.count")} hint={userId ? t("enrollment.userBinding.singleHint") : undefined}>
        {(id) => (
          <Input
            id={id}
            type="number"
            min={1}
            max={500}
            value={effectiveCount}
            onChange={(e) => setCount(e.target.value)}
            disabled={Boolean(userId)}
          />
        )}
      </FormField>

      <FormField label={t("enrollment.tokens.field.expiresAt")} required>
        {(id) => (
          <DateTimeField
            id={id}
            value={expiresAt}
            onChange={setExpiresAt}
            ariaLabel={t("enrollment.tokens.field.expiresAt")}
            required
          />
        )}
      </FormField>

      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
        <Button type="submit" loading={mutate.isPending}>
          {t("enrollment.tokens.submit")}
        </Button>
      </div>
    </form>
  );
}
